import { Image as ImageIcon, Palette } from "lucide-react";
import { StudioPageLayout } from "@/components/flowly/StudioPageLayout/StudioPageLayout";
import { MediaGridItem } from "@/components/flowly/MediaGridItem/MediaGridItem";
import { CreationsGallery } from "@/components/flowly/CreationsGallery/CreationsGallery";
import { GalleryItemBadges } from "@/components/flowly/GalleryItemBadges/GalleryItemBadges";
import { GenerationBar } from "@/components/flowly/GenerationBar/GenerationBar";
import { ImageLightbox } from "@/components/flowly/Lightbox/ImageLightbox/ImageLightbox";

import { HowItWorks } from "@/components/flowly/HowItWorks/HowItWorks";
import { ImageUploadGuide } from "@/components/flowly/ImageUploadGuide/ImageUploadGuide";
import { StudioHero } from "@/components/flowly/StudioHero/StudioHero";
import { StudioPresetGrid } from "@/components/flowly/StudioPresetGrid/StudioPresetGrid";
import { FlowReadyCta } from "@/components/flowly/FlowReadyCta/FlowReadyCta";
import { downloadMedia } from "@/lib/mediaUtils";
import { useGenerateProductCatalog } from "./useGenerateProductCatalog";
import {
  STUDIO_PRESETS_ANCHOR_ID,
  STUDIO_CONFIG_ANCHOR_ID,
  STUDIO_ADVANCED_PRESET_ID,
  STUDIO_BACKGROUND_QUICK_IDEAS,
} from "./constants";
import { AD_IMAGES_PER_GENERATION } from "@shared/schemas/enums";

export const GenerateProductCatalogPage = () => {
  const {
    galleryRef,
    file,
    filePreview,
    additionalImages,
    setAdditionalImages,
    handleFileSelect,
    clearFile,
    aspectRatio,
    setAspectRatio,
    resolution,
    availableResolutions,
    handleResolutionChange,
    generating,
    pendingImages,
    dbPendingImages,
    imageCompletingIds,
    failedImages,
    handleClearFailed,
    handleGenerate,
    getImageCreditCost,
    getImageModelCost,
    costsLoading,
    galleryImages,
    models,
    modelWarning,
    imageModel,
    handleImageModelChange,
    selectedMedia,
    setSelectedMedia,
    handleDownload,
    handleShare,
    handleRegenerate,
    handleUseSourceImage,
    handleDelete,
    handleCloseLightbox,
    currentMediaIndex,
    canNavigate,
    goToNextItem,
    goToPrevItem,
    userPrompt,
    setUserPrompt,
    availableAspectRatios,
    handleDisabledClick,
    presetId,
    handlePresetSelect,
    latestGenerationStartedAt,
    language,
    locale,
    t,
  } = useGenerateProductCatalog();

  const hasUpload = !!file || !!filePreview;
  const isAdvanced = presetId === STUDIO_ADVANCED_PRESET_ID;
  // Hero "after" thumbnails — only show results from the user's *current*
  // session, so changing the source photo wipes the demo. `null` boundary
  // = no run yet, render an empty stack with the "Create now" CTA instead.
  const studioResultPreviews =
    latestGenerationStartedAt === null
      ? []
      : galleryImages
          .filter(
            (img) =>
              new Date(img.createdAt).getTime() >= latestGenerationStartedAt,
          )
          .slice(0, AD_IMAGES_PER_GENERATION)
          .map((img) => img.displayUrl);

  return (
    <StudioPageLayout
      activeTab="marketing"
      activeApp="ads"
      headerChildren={
        <>
          <HowItWorks flowId="productCatalog" />
          <ImageUploadGuide />
        </>
      }
      footer={
        !selectedMedia ? (
          <GenerationBar
            onGenerate={handleGenerate}
            generating={generating}
            disabled={!hasUpload || !presetId}
            creditCost={getImageCreditCost(resolution, imageModel)}
            costsLoading={costsLoading}
            buttonLabel={t.productStudio.generateBtn}
            buttonTooltip={
              !hasUpload ? t.generate.insertImageHint : undefined
            }
            aspectRatio={aspectRatio}
            onAspectRatioChange={setAspectRatio}
            availableAspectRatios={availableAspectRatios}
            resolution={resolution}
            onResolutionChange={handleResolutionChange}
            availableImageResolutions={availableResolutions}
            modelWarning={modelWarning}
            singleModelTooltip={t.generate.bestModelForFlow}
            selectedModelId={imageModel}
            onModelChange={handleImageModelChange}
            mediaType="image"
            models={models}
            getModelCost={(id) => getImageModelCost(id, models, resolution)}
            onDisabledClick={handleDisabledClick}
          />
        ) : undefined
      }
      lightbox={
        selectedMedia ? (
          <ImageLightbox
            imageUrl={selectedMedia.url}
            imageId={selectedMedia.id}
            referenceImageUrl={selectedMedia.referenceImageUrls[0]}
            createdAt={selectedMedia.createdAt}
            resolution={selectedMedia.resolution}
            aspectRatio={selectedMedia.aspectRatio}
            pipeline={selectedMedia.pipeline}
            prompt={selectedMedia.prompt}
            model={selectedMedia.model}
            onClose={handleCloseLightbox}
            onRegenerate={handleRegenerate}
            onUseSourceImage={handleUseSourceImage}
            onDownload={handleDownload}
            onShare={handleShare}
            onDelete={handleDelete}
            navigation={
              galleryImages.length > 1
                ? {
                    currentIndex: currentMediaIndex,
                    totalItems: galleryImages.length,
                    canGoNext: canNavigate,
                    canGoPrev: canNavigate,
                    onNext: goToNextItem,
                    onPrev: goToPrevItem,
                  }
                : undefined
            }
          />
        ) : undefined
      }
    >
      <div data-scroll-target="upload" className="space-y-6 md:space-y-8">
        <StudioHero
          uploadPreview={filePreview}
          isUploading={false}
          isGenerating={generating}
          results={studioResultPreviews}
          expectedResultCount={AD_IMAGES_PER_GENERATION}
          canGenerate={hasUpload && !!presetId}
          presetsAnchorId={STUDIO_PRESETS_ANCHOR_ID}
          additionalImages={additionalImages}
          onAdditionalImagesChange={setAdditionalImages}
          onSelectFile={handleFileSelect}
          onClearUpload={clearFile}
          onGenerate={handleGenerate}
        />

        {hasUpload && (
          <div
            id={STUDIO_PRESETS_ANCHOR_ID}
            className="scroll-mt-24 rounded-2xl border border-brand/30 bg-brand/[0.04] p-4 md:p-5 animate-in fade-in slide-in-from-bottom-2 duration-300"
          >
            <StudioPresetGrid selectedId={presetId} onSelect={handlePresetSelect} />
          </div>
        )}

        {hasUpload && isAdvanced && (
          <div
            id={STUDIO_CONFIG_ANCHOR_ID}
            className="scroll-mt-24 relative overflow-hidden rounded-3xl border border-brand/30 bg-gradient-to-br from-brand/[0.07] via-brand/[0.02] to-transparent p-5 md:p-7 shadow-[0_0_40px_hsl(var(--brand)/0.08)] animate-in fade-in slide-in-from-bottom-2 duration-300"
          >
            {/* Decorative blob — purely visual depth */}
            <div className="absolute -top-16 -end-16 w-40 h-40 rounded-full bg-brand/15 blur-3xl pointer-events-none" />

            <div className="relative">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br gradient-primary flex items-center justify-center shadow-[0_0_20px_hsl(var(--brand)/0.4),0_4px_12px_rgba(0,0,0,0.15)] shrink-0">
                  <Palette className="w-5 h-5 text-white" strokeWidth={2.25} />
                </div>
                <div>
                  <label
                    htmlFor="studio-custom-background"
                    className="block text-base md:text-lg font-bold text-foreground"
                  >
                    {t.productStudio.customBackgroundLabel}
                  </label>
                  <p className="text-xs md:text-sm text-muted-foreground mt-0.5">
                    {t.productStudio.customBackgroundHelp}
                  </p>
                </div>
              </div>

              <textarea
                id="studio-custom-background"
                value={userPrompt}
                onChange={(e) => setUserPrompt(e.target.value)}
                placeholder={t.productStudio.customBackgroundPlaceholder}
                dir={language === "he" ? "rtl" : "ltr"}
                className="w-full h-28 md:h-32 bg-card/90 backdrop-blur-sm border-2 border-border/60 rounded-2xl p-4 text-sm md:text-base text-foreground placeholder:text-muted-foreground/55 resize-none focus:outline-none focus:border-brand focus:bg-card focus:shadow-[0_0_25px_hsl(var(--brand)/0.18)] hover:border-brand/40 transition-all duration-200"
              />

              <div className="mt-4">
                <p className="text-[11px] md:text-xs font-semibold text-muted-foreground/80 mb-2">
                  ✨ {t.productStudio.customBackgroundQuickIdeas}
                </p>
                <div className="flex flex-wrap gap-2">
                  {STUDIO_BACKGROUND_QUICK_IDEAS.map((idea) => {
                    const labels = t.productStudio.quickBackgrounds as Record<
                      string,
                      string
                    >;
                    const label = labels[idea.key] ?? idea.key;
                    return (
                      <button
                        key={idea.key}
                        type="button"
                        onClick={() => setUserPrompt(label)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border bg-card text-xs md:text-sm font-medium text-foreground hover:border-brand/50 hover:bg-brand/8 hover:text-brand active:scale-95 transition-all"
                      >
                        <span aria-hidden>{idea.emoji}</span>
                        <span>{label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Inline "all set" CTA — same generate action as the footer bar. */}
        {hasUpload && !!presetId && (
          <FlowReadyCta
            onGenerate={handleGenerate}
            generating={generating}
            creditCost={getImageCreditCost(resolution, imageModel)}
            costsLoading={costsLoading}
            buttonLabel={t.productStudio.generateBtn}
          />
        )}
      </div>

      {/* User Creations Gallery */}
      <CreationsGallery
        title={t.generate.yourCreations}
        galleryRef={galleryRef}
        pendingCount={pendingImages}
        pendingLabel={t.generate.processing}
        pendingItems={dbPendingImages.map((img) => ({ id: img.id, createdAt: img.created_at }))}
        pendingMediaType="image"
        completingIds={imageCompletingIds}
        failedItems={failedImages}
        failedLabel={t.generate.failedRefunded}
        onClearFailed={handleClearFailed}
        className="mt-10 mb-10"
        emptyIcon={ImageIcon}
        emptyTitle={t.generate.noCreationsYet}
        emptySubtitle={t.generate.startCreating}
      >
        {galleryImages.map((img, idx) => (
          <MediaGridItem
            key={img.id}
            imageUrl={img.displayUrl}
            hoverText={t.generate.clickToOpen}
            alt={`Creation ${idx + 1}`}
            onClick={() => setSelectedMedia(img)}
            onDownload={() => downloadMedia(img.url, "todoai-creation", "png")}
            downloadLabel={t.lightbox.download}
          >
            <GalleryItemBadges item={img} />
          </MediaGridItem>
        ))}
      </CreationsGallery>
    </StudioPageLayout>
  );
};

export default GenerateProductCatalogPage;
